import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  IoMailOutline,
  IoLockClosedOutline,
  IoPersonOutline,
  IoCardOutline,
} from 'react-icons/io5'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { authService } from '../services/api/auth'
import { useAuthStore } from '../stores'
import type { ApiResponse } from '../types'
import type { AuthResponse } from '../services/api/auth'

export default function Register() {
  const navigate = useNavigate()
  const loginStore = useAuthStore(state => state.login)
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    licenseNumber: '',
  })
  const [loading, setLoading] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.email.trim() ||
      !form.password ||
      !form.licenseNumber.trim()
    ) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }
    setLoading(true)
    try {
      const response = (await authService.register({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        password: form.password,
        licenseNumber: form.licenseNumber.trim(),
      })) as ApiResponse<AuthResponse>
      const authData = response.data
      if (
        !authData?.user ||
        !authData?.accessToken ||
        !authData?.refreshToken
      ) {
        toast.error('Réponse serveur invalide')
        return
      }
      loginStore(authData.user, authData.accessToken, authData.refreshToken)
      toast.success('Compte créé avec succès')
      navigate('/', { replace: true })
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : "Erreur lors de l'inscription"
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-dark-50 flex items-center justify-center px-4 py-12'>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className='w-full max-w-md'
      >
        <div className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl p-8'>
          <div className='flex justify-center mb-2'>
            <img src='/medisync_logo.png' alt='MediSync' className='h-16 w-auto' />
          </div>
          <p className='text-gray-600 dark:text-gray-400 text-center mb-8'>
            Créer un compte infirmier
          </p>

          <form onSubmit={handleSubmit} className='space-y-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Prénom
                </label>
                <div className='relative flex items-center'>
                  <IoPersonOutline className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 shrink-0 pointer-events-none' />
                  <input
                    type='text'
                    name='firstName'
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder='Prénom'
                    className='input-field input-with-icon-left w-full'
                  />
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                  Nom
                </label>
                <div className='relative flex items-center'>
                  <IoPersonOutline className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 shrink-0 pointer-events-none' />
                  <input
                    type='text'
                    name='lastName'
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder='Nom'
                    className='input-field input-with-icon-left w-full'
                  />
                </div>
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                Email
              </label>
              <div className='relative flex items-center'>
                <IoMailOutline className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 shrink-0 pointer-events-none' />
                <input
                  type='email'
                  name='email'
                  value={form.email}
                  onChange={handleChange}
                  placeholder='vous@exemple.com'
                  className='input-field input-with-icon-left w-full'
                />
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                Numéro d'ordre / licence
              </label>
              <div className='relative flex items-center'>
                <IoCardOutline className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 shrink-0 pointer-events-none' />
                <input
                  type='text'
                  name='licenseNumber'
                  value={form.licenseNumber}
                  onChange={handleChange}
                  placeholder='Numéro de licence'
                  className='input-field input-with-icon-left w-full'
                />
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                Mot de passe
              </label>
              <div className='relative flex items-center'>
                <IoLockClosedOutline className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 shrink-0 pointer-events-none' />
                <input
                  type='password'
                  name='password'
                  value={form.password}
                  onChange={handleChange}
                  placeholder='••••••••'
                  className='input-field input-with-icon-left w-full'
                />
              </div>
            </div>

            <button
              type='submit'
              disabled={loading}
              className='btn-primary w-full py-3 disabled:opacity-50 mt-4'
            >
              {loading ? 'Inscription...' : "S'inscrire"}
            </button>
          </form>

          <p className='mt-6 text-center text-gray-600 dark:text-gray-400 text-sm'>
            Déjà un compte ?{' '}
            <Link
              to='/login'
              className='text-primary-400 hover:underline font-medium'
            >
              Se connecter
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  )
}
